// Strategy (MicroStrategy) to Amazon Quick page content. Same shape as
// domo.js; rendered by PlatformMigrationContent (components/BiPlatformPage).
export const strategyPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Strategy to Amazon Quick',
    intro: 'The reasons we hear most from Strategy (MicroStrategy) customers running on AWS.',
    cards: [
      { title: 'A lighter platform to run', description: 'No Intelligence Servers, metadata repositories, or upgrade projects to manage. Quick is fully managed on AWS.' },
      { title: 'Your data is already on AWS', description: 'Quick reads Redshift, S3, and Athena directly, so Intelligent Cubes and extra copies can be retired.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Strategy metadata',
    title: 'We read your Strategy metadata through its APIs',
    intro: 'Instead of weeks of interviews, we pull metadata straight from Strategy. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Attributes, facts, hierarchies, and tables', from: 'REST and Modeling APIs', use: 'Map the schema to Quick datasets and size the data landing in Amazon Redshift' },
      { what: 'Metrics, filters, and prompts', from: 'REST and Modeling APIs', use: 'Convert metrics to calculated fields and prompts to Quick controls' },
      { what: 'Dossiers and dashboards', from: 'Library REST API', use: 'Rebuild as interactive Quick Sight dashboards and spot duplicates' },
      { what: 'Documents and grid reports', from: 'REST API and metadata', use: 'Rebuild as paginated reports with the same layout and parameters' },
      { what: 'Security filters, users, and groups', from: 'REST API', use: 'Recreate the same row-level security and map to SSO and IAM groups' },
      { what: 'Report and dossier usage', from: 'Platform Analytics or Enterprise Manager', use: 'Find the most-used and unused content to rank waves and retire dead content' },
      { what: 'Intelligent Cubes, schedules, and subscriptions', from: 'REST API', use: 'Plan SPICE refreshes and move subscriptions to scheduled reports' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every dossier traced back through its metrics and attributes to the warehouse table.' },
        { title: 'Complexity score per object', description: 'Metrics, prompts, and security filters scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by Platform Analytics, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Strategy through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Strategy admin creates a service account with read-only privileges. Nothing in Strategy changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Strategy migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every report.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled through the Strategy APIs: schema objects, metrics, dossiers, documents, security filters, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Content grouped into waves by usage, shared schema objects, and project. Unused reports are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Schema objects mapped to datasets, metrics converted to calculated fields, and dossiers and documents rebuilt.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated report compared with its Strategy original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Strategy content today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Strategy reports used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
